import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Star, Crown, ArrowRight } from 'lucide-react';
import { TIERS } from '../config/tiers';
import { useNavigate } from 'react-router-dom';

interface TierUpgradeModalSimpleProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: string;
  upgradeReason?: 'customer' | 'karigar' | 'order' | 'general';
}

const TierUpgradeModalSimple: React.FC<TierUpgradeModalSimpleProps> = ({
  isOpen,
  onClose,
  currentTier,
  upgradeReason
}) => {
  const navigate = useNavigate();

  const getUpgradeReasonMessage = () => {
    switch (upgradeReason) {
      case 'customer':
        return 'You\'ve reached your customer limit. Upgrade to add more customers.';
      case 'karigar':
        return 'You\'ve reached your karigar limit. Upgrade to add more karigars.';
      case 'order':
        return 'You\'ve reached your order limit. Upgrade to add more orders.';
      default:
        return 'Unlock more features and higher limits for your business.';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-blue to-brand-blue/90 text-white p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Upgrade Your Plan</h2>
                <p className="text-brand-blue-100 mt-1">
                  {getUpgradeReasonMessage()}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-brand-blue-100 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-brand-blue to-brand-blue/80 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Ready to Scale Your Business?
              </h3>
              <p className="text-gray-600 mb-6">
                Choose from our flexible plans designed to grow with your tailoring business.
              </p>
            </div>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-700">Advanced Analytics</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-700">Priority Support</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-700">Custom Integrations</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-700">Higher Limits</span>
              </div>
            </div>

            {/* Current Plan Info */}
            {currentTier && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    Current Plan: {TIERS[currentTier]?.name || 'Free'}
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                className="w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 bg-gradient-to-r from-brand-blue to-brand-blue/90 text-white hover:shadow-lg hover:shadow-brand-blue/25 flex items-center justify-center space-x-2"
              >
                <span>View All Plans</span>
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              
              <button
                onClick={onClose}
                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors font-medium border border-gray-300 rounded-lg hover:border-gray-400"
              >
                Maybe Later
              </button>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center">
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>30-day guarantee</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Cancel anytime</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>No setup fees</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TierUpgradeModalSimple;
